import React from 'react'
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core'

const confidenceChip = (confidence) => {
  switch ((confidence || '').toLowerCase()) {
    case 'high':
      return { label: 'High confidence', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    case 'medium':
      return { label: 'Medium confidence', style: { backgroundColor: '#fff9c4', color: '#f57f17' } }
    case 'low':
      return { label: 'Low confidence', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
    default:
      return { label: confidence || 'Unknown', style: { backgroundColor: '#eeeeee', color: '#616161' } }
  }
}

const ImportTableCard = ({
  sheetIndex,
  tableIndex,
  table,
  buildings,
  state,
  onChange
}) => {
  const rows = Array.isArray(table.rows) ? table.rows : []
  const skipSet = state.skipSet || new Set()
  const chip = confidenceChip(table.confidence)

  // Auto-skip duplicate rows on first render
  React.useEffect(() => {
    const dupeIndexes = rows
      .map((row, idx) => (row.isDuplicate ? idx : null))
      .filter(idx => idx !== null)
    if (dupeIndexes.length > 0 && skipSet.size === 0) {
      onChange(sheetIndex, tableIndex, {
        ...state,
        skipSet: new Set(dupeIndexes)
      })
    }
  }, []) // eslint-disable-line

  const importedCount = rows.length - skipSet.size

  const toggleRow = (idx) => {
    const next = new Set(skipSet)
    if (next.has(idx)) next.delete(idx)
    else next.add(idx)
    onChange(sheetIndex, tableIndex, { ...state, skipSet: next })
  }

  const toggleAll = () => {
    if (skipSet.size === 0) {
      onChange(sheetIndex, tableIndex, {
        ...state,
        skipSet: new Set(rows.map((_, i) => i))
      })
    } else {
      onChange(sheetIndex, tableIndex, { ...state, skipSet: new Set() })
    }
  }

  const allChecked = skipSet.size === 0
  const someChecked = skipSet.size > 0 && skipSet.size < rows.length

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        flexWrap: 'wrap'
      }}>
        <Chip
          size="small"
          label={chip.label}
          style={{ ...chip.style, height: 22 }}
        />
        <Typography variant="caption" color="textSecondary">
          {importedCount} of {rows.length} rows will be imported
        </Typography>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {buildings.length > 0 ? (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Building</InputLabel>
            <Select
              value={state.building || ''}
              onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, building: e.target.value })}
              label="Building"
            >
              {buildings.map(b => {
                const name = typeof b === 'string' ? b : (b.name || '')
                return (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                )
              })}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Building"
            variant="outlined"
            size="small"
            value={state.building || ''}
            onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, building: e.target.value })}
            style={{ minWidth: 200 }}
            placeholder="e.g. Main"
          />
        )}
        <TextField
          label="Default section (optional)"
          variant="outlined"
          size="small"
          value={state.section || ''}
          onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, section: e.target.value })}
          style={{ minWidth: 220 }}
          helperText="Used when a row has no section"
        />
      </div>

      <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #eeeeee', borderRadius: 4 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" style={{ backgroundColor: '#fafafa' }}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={toggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell style={{ backgroundColor: '#fafafa', width: 40 }}>#</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Item No</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Description</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Unit</TableCell>
              <TableCell align="right" style={{ backgroundColor: '#fafafa' }}>Qty</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Section</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="caption" color="textSecondary">
                    No rows in this table.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, i) => {
              const skipped = skipSet.has(i)
              const isDupe = row.isDuplicate
              return (
                <TableRow
                  key={i}
                  hover
                  style={{
                    opacity: skipped ? 0.45 : 1,
                    textDecoration: skipped ? 'line-through' : 'none',
                    backgroundColor: isDupe && !skipped ? '#fff3e0' : undefined
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!skipped}
                      onChange={() => toggleRow(i)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{row.itemNo != null ? row.itemNo : ''}</TableCell>
                  <TableCell style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {row.description || ''}
                    {isDupe && (
                      <Chip
                        size="small"
                        label="Duplicate"
                        style={{
                          marginLeft: 8,
                          height: 18,
                          fontSize: 10,
                          backgroundColor: '#ff9800',
                          color: '#fff'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{row.unit || ''}</TableCell>
                  <TableCell align="right">{row.quantity != null ? row.quantity : ''}</TableCell>
                  <TableCell>{row.section || ''}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default ImportTableCard
